import { useMemo } from 'react';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import stylesParams, { PresetSettings } from '../../stylesParams';
import settingsParams from '../../settingsParams';
import useObservable from '../../Shared/Hooks/useObservable.Hook';
import {
  FILE_SIZE_NUMBER_OF_ITEMS_KEY,
  LAST_UPDATED_KEY,
  VIEWS_COUNTER_KEY,
  FAVORITES_COUNTER_KEY,
  CONTRIBUTORS_KEY,
} from '../../Constants/Display.Constants';
import { getSettingsData } from '../Services/AppSettings.service';
import { FileIcon } from '@wix/ambassador-file-sharing-v1-settings/types';
import { fileIconType } from '../../Constants/fileIconType';

type WidgetStyleParams = {
  showFileSize: boolean;
  showLastModified: boolean;
  showViewsCounter: boolean;
  showFavoritesCounter: boolean;
  showFileOwnerName: boolean;
  isEditor3: boolean;
  fileIcon: FileIcon;
};

const useWidgetStyleParams = (): WidgetStyleParams => {
  const styles = useStyles();
  const settings = useSettings();
  const [, settingsData]: any = useObservable(getSettingsData());

  const isEditor3 = settings.get(settingsParams.isEditor3) ?? false;

  const showFileSizeDefault =
    settingsData?.settings?.displaySettings?.[FILE_SIZE_NUMBER_OF_ITEMS_KEY];
  const showLastModifiedDefault =
    settingsData?.settings?.displaySettings?.[LAST_UPDATED_KEY];
  const showViewsCounterDefault =
    settingsData?.settings?.displaySettings?.[VIEWS_COUNTER_KEY];
  const showFavoritesCounterDefault =
    settingsData?.settings?.displaySettings?.[FAVORITES_COUNTER_KEY];
  const showFileOwnerNameDefault =
    settingsData?.settings?.displaySettings?.[CONTRIBUTORS_KEY];
  const fileIconDefault =
    settingsData?.settings?.designSettings?.fileIcon ?? FileIcon.CLASSIC;

  type GetStyleValueReturnType = ReturnType<typeof styles.get>;

  return useMemo(() => {
    let showFileSize = showFileSizeDefault;
    let showLastModified = showLastModifiedDefault;
    let showViewsCounter = showViewsCounterDefault;
    let showFavoritesCounter = showFavoritesCounterDefault;
    let showFileOwnerName = showFileOwnerNameDefault;
    let fileIcon = fileIconDefault;

    const getStyleValue = (
      styleParam: any,
      defaultValue: any,
    ): GetStyleValueReturnType => {
      const styleValue: GetStyleValueReturnType = styles.get(styleParam);
      const styleValueType = typeof styleValue;
      return styleValueType === 'boolean' || styleValueType === 'number'
        ? styleValue
        : defaultValue;
    };

    if (isEditor3) {
      showFileSize = getStyleValue(
        stylesParams.showFileSize,
        showFileSizeDefault,
      );
      showLastModified = getStyleValue(
        stylesParams.showLastModified,
        showLastModifiedDefault,
      );
      showViewsCounter = getStyleValue(
        stylesParams.showViewsCounter,
        showViewsCounterDefault,
      );
      showFavoritesCounter = getStyleValue(
        stylesParams.showFavoritesCounter,
        showFavoritesCounterDefault,
      );
      showFileOwnerName = getStyleValue(
        stylesParams.showFileOwnerName,
        showFileOwnerNameDefault,
      );
      fileIcon = getStyleValue(stylesParams.presetSettings, fileIconDefault);
      switch (fileIcon) {
        case PresetSettings.LABEL_STYLE:
        case FileIcon.EXTENSION:
        case fileIconType.LABEL_ICON:
          fileIcon = FileIcon.EXTENSION;
          break;
        case FileIcon.LINE:
        case fileIconType.LINE_ICON:
          fileIcon = FileIcon.LINE;
          break;
        case PresetSettings.FILL_STYLE:
        default:
          fileIcon = fileIconType.FILL_ICON;
      }
    }

    return {
      showFileSize,
      showLastModified,
      showViewsCounter,
      showFavoritesCounter,
      showFileOwnerName,
      isEditor3,
      fileIcon,
    };
  }, [
    isEditor3,
    styles,
    showFileSizeDefault,
    showLastModifiedDefault,
    showViewsCounterDefault,
    showFavoritesCounterDefault,
    showFileOwnerNameDefault,
    fileIconDefault,
  ]);
};

export default useWidgetStyleParams;
